import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Ticket, Plus, Trash2, Percent, TrendingUp, Download } from "lucide-react";
import { toast } from "sonner";
import { api, local, errMsg } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, money } from "@/lib/format";
import { SATrialPromos } from "@/components/SATrialPromos";
import dayjs from "dayjs";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

export function SAPromos() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", title: "", description: "", discount_percent: 10, valid_until: "", active: true });

  const { data: promos = [], isLoading } = useQuery({ queryKey: ["sa-promos"], queryFn: async () => (await api.get("/superadmin/promos")).data || [] });
  const { data: redResp } = useQuery({ queryKey: ["promo-redemptions"], queryFn: async () => (await local.get("/promo/redemptions")).data });
  const redemptions = redResp?.redemptions || [];
  const statFor = (code) => {
    const rows = redemptions.filter((r) => (r.promo_code || "").toUpperCase() === (code || "").toUpperCase());
    const orgs = new Set(rows.map((r) => r.org_id)).size;
    const gross = rows.reduce((s, r) => s + (Number(r.plan_price) || 0), 0);
    return { orgs, gross, cur: rows[0]?.currency || "NGN" };
  };
  const monthly = (() => {
    const m = {};
    redemptions.forEach((r) => {
      const key = dayjs(r.redeemed_at).format("MMM YY");
      if (!m[key]) m[key] = { month: key, count: 0, revenue: 0, _t: dayjs(r.redeemed_at).valueOf() };
      m[key].count += 1;
      m[key].revenue += Number(r.plan_price) || 0;
    });
    return Object.values(m).sort((a, b) => a._t - b._t);
  })();

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/superadmin/promos", { ...form, discount_percent: Number(form.discount_percent), valid_until: form.valid_until || null })).data,
    onSuccess: () => { toast.success("Promo created"); qc.invalidateQueries({ queryKey: ["sa-promos"] }); setOpen(false); setForm({ code: "", title: "", description: "", discount_percent: 10, valid_until: "", active: true }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggleMut = useMutation({
    mutationFn: async (p) => (await api.put(`/superadmin/promos/${p.id}`, { ...p, active: !p.active })).data,
    onSuccess: () => { toast.success("Promo updated"); qc.invalidateQueries({ queryKey: ["sa-promos"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/superadmin/promos/${id}`)).data,
    onSuccess: () => { toast.success("Promo deleted"); qc.invalidateQueries({ queryKey: ["sa-promos"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const exportCSV = () => {
    const header = ["code", "title", "discount_percent", "orgs_redeemed", "gross_revenue", "discount_given", "net_revenue", "currency"];
    const rows = promos.map((p) => {
      const st = statFor(p.code); const d = Number(p.discount_percent) || 0; const saved = st.gross * d / 100;
      return [p.code, `"${(p.title || "").replace(/"/g, '""')}"`, d, st.orgs, st.gross, saved, st.gross - saved, st.cur];
    });
    const csv = [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `fhapas-promo-report-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Promos & Offers</h1>
          <p className="mt-1 text-sm text-slate-400">Discount codes for organisation subscriptions</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="sa-promo-export" variant="outline" className="gap-1.5 border-white/15 bg-white/5 text-slate-200 hover:bg-white/10" onClick={exportCSV}><Download className="h-4 w-4" /> Export CSV</Button>
          <Button data-testid="sa-add-promo" className="gap-1.5 bg-teal-600 hover:bg-teal-700" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New promo</Button>
        </div>
      </div>

      {monthly.length > 0 && (
        <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="promo-trends">
          <h2 className="mb-4 flex items-center gap-2 font-display text-base font-semibold text-white"><TrendingUp className="h-4 w-4 text-teal-400" /> Redemption trends</h2>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} stroke="#334155" />
              <YAxis yAxisId="l" tick={{ fontSize: 12, fill: "#94a3b8" }} stroke="#334155" allowDecimals={false} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 12, fill: "#94a3b8" }} stroke="#334155" />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
              <Bar yAxisId="l" dataKey="count" name="Redemptions" fill="#14b8a6" radius={[6, 6, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="revenue" name="Revenue" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {isLoading ? <Loader full /> : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {promos.map((p) => (
            <div key={p.id} data-testid={`sa-promo-${p.code}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-start justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-600/20 text-teal-400"><Ticket className="h-5 w-5" /></div>
                <StatusBadge value={p.active ? "active" : "cancelled"} />
              </div>
              <p className="mt-3 font-mono text-sm font-bold text-teal-400">{p.code}</p>
              <p className="font-display text-base font-semibold text-white">{p.title}</p>
              {p.description && <p className="mt-1 text-sm text-slate-400">{p.description}</p>}
              <div className="mt-3 flex items-center gap-2 text-sm text-slate-300"><Percent className="h-4 w-4 text-emerald-400" /> {p.discount_percent}% off {p.valid_until && <span className="text-slate-500">· until {fmtDate(p.valid_until)}</span>}</div>
              {(() => { const st = statFor(p.code); const saved = st.gross * (Number(p.discount_percent) || 0) / 100; return (
                <div className="mt-3 grid grid-cols-3 gap-2 rounded-lg bg-white/[0.04] p-2 text-center" data-testid={`promo-analytics-${p.code}`}>
                  <div><p className="font-display text-lg font-bold text-white">{st.orgs}</p><p className="text-[10px] uppercase tracking-wide text-slate-400">Orgs</p></div>
                  <div><p className="font-display text-sm font-bold text-emerald-400">{money(st.gross, st.cur)}</p><p className="text-[10px] uppercase tracking-wide text-slate-400">Gross</p></div>
                  <div><p className="font-display text-sm font-bold text-amber-400">{money(saved, st.cur)}</p><p className="text-[10px] uppercase tracking-wide text-slate-400">Discount</p></div>
                </div>
              ); })()}
              <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
                <div className="flex items-center gap-2 text-xs text-slate-400"><Switch data-testid={`sa-promo-toggle-${p.code}`} checked={!!p.active} onCheckedChange={() => toggleMut.mutate(p)} /> {p.active ? "Active" : "Inactive"}</div>
                <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" data-testid={`sa-promo-delete-${p.code}`} onClick={() => delMut.mutate(p.id)}><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <SATrialPromos />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">New promo code</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Code</Label><Input data-testid="promo-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LAUNCH50" /></div>
              <div className="space-y-1.5"><Label>Discount %</Label><Input data-testid="promo-discount" type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="promo-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Launch offer" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="promo-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="space-y-1.5"><Label>Valid until (optional)</Label><Input data-testid="promo-valid" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="promo-save" disabled={!form.code || !form.title || createMut.isPending} className="bg-teal-600 hover:bg-teal-700" onClick={() => createMut.mutate()}>{createMut.isPending ? "Creating…" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
